'use client';

import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Cursor } from 'motion-plus/react';
import Image from 'next/image';
import { Link } from 'next-view-transitions';
import { ComponentType, Dispatch, SetStateAction, SVGProps, useEffect, useState } from 'react';

import { SvgCollaborate, SvgIntelExchange, SvgOrchestrate, SvgRespond } from '@/app/components/ui/SVG Components/SvgProducts';
import { getHref } from '@/app/lib/utils/getHref';
import type { ContenfulProductItem, ProductItemsCollection } from '@/app/types/contentful/types';

export type ProductDetails = {
  SvgComponent: ComponentType<SVGProps<SVGSVGElement>>;
  backgroundColor: string;
  borderColor: string;
  hoverShadow: string;
  hoverRing: string;
  focusRing: string;
  connectorHover?: string;
  cursorTextColor: string;
};

// Product details map
export const productDetailsMap: Record<string, ProductDetails> = {
  'Intel Exchange': {
    SvgComponent: SvgIntelExchange,
    backgroundColor: '#0C195A',
    borderColor: '#8ABEFF',
    hoverShadow: 'shadow-blue-400',
    hoverRing: 'ring-blue-400',
    focusRing: 'focus:ring-blue-400',
    connectorHover: 'group-hover:bg-blue-300',
    cursorTextColor: '#8ABEFF',
  },
  Collaborate: {
    SvgComponent: SvgCollaborate,
    backgroundColor: '#012D23',
    borderColor: '#65FC9F',
    hoverShadow: 'shadow-green-400',
    hoverRing: 'ring-green-400',
    focusRing: 'focus:ring-green-400',
    connectorHover: 'group-hover:bg-green-300',
    cursorTextColor: '#65FC9F',
  },
  Respond: {
    SvgComponent: SvgRespond,
    backgroundColor: '#4D005C',
    borderColor: '#F19CFF',
    hoverShadow: 'shadow-purple-400',
    hoverRing: 'ring-purple-400',
    focusRing: 'focus:ring-purple-400',
    connectorHover: 'group-hover:bg-purple-300',
    cursorTextColor: '#F19CFF',
  },
  Orchestrate: {
    SvgComponent: SvgOrchestrate,
    backgroundColor: '#0C195A',
    borderColor: '#83CAFF',
    hoverShadow: 'shadow-blue-600',
    hoverRing: 'ring-blue-600',
    focusRing: 'focus:ring-blue-600',
    cursorTextColor: '#83CAFF',
  },
};

// Custom cursor styles
const cursorStyle: React.CSSProperties = {
  backgroundColor: 'rgba(48, 48, 48, 0.8)',
  backdropFilter: 'blur(5px)',
  padding: '5px 10px',
  borderRadius: 10,
  zIndex: 9999,
};

export default function ProductItems({ collection }: { collection: ProductItemsCollection }) {
  const [activeItem, setActiveItem] = useState<ContenfulProductItem | null>(null);
  const [isHovering, setIsHovering] = useState<{ title: string; isHovered: boolean; cursorTextColor: string }>({
    title: '',
    isHovered: false,
    cursorTextColor: '',
  });

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeItem) {
        setActiveItem(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    // Prevent scrolling when modal is open
    if (activeItem) {
      document.body.style.overflow = 'clip';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [activeItem]);

  const topRowItems = collection.items.slice(0, 3); // Index 0, 1, 2
  const bottomRowItem = collection.items[3]; // Index 3

  return (
    <article className="col-span-full w-full px-8">
      <AnimatePresence mode="wait">
        {isHovering.isHovered && (
          <Cursor
            style={cursorStyle}
            follow
            offset={{ x: 10, y: 10 }}
            variants={{ initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.9 } }}
            transition={{ duration: 0.3 }}
            key="product-tooltip"
          >
            <p style={{ color: isHovering.cursorTextColor }}>Click to learn more about {isHovering.title}</p>
          </Cursor>
        )}
      </AnimatePresence>

      <nav className="w-full space-y-18 md:space-y-16" aria-label="Product cards">
        {/* Top Row Cards */}
        <ul className="grid auto-rows-[minmax(95px,1fr)] grid-cols-3 gap-6 md:grid-cols-1" aria-label="Core product offerings">
          {topRowItems.map(item => {
            const details = productDetailsMap[item.buttonTitle];
            if (!details) return null;
            const { SvgComponent, hoverShadow, hoverRing, connectorHover, focusRing } = details;

            return (
              <li key={item._id} className="contents">
                <motion.button
                  key={item._id}
                  aria-label={item.buttonTitle}
                  className={`group relative flex touch-manipulation items-center justify-center rounded-lg border-[3px] border-gray-600 bg-SLATE_BLUE px-4 transition-colors duration-300 focus:outline-none focus:ring-2 ${focusRing} focus:ring-offset-2 focus:ring-offset-SLATE_BLUE min-md:hover:bg-SLATE-BLUE-950 min-md:hover:shadow-[0_0_10px_rgba(0,0,0,0.5)] min-md:hover:${hoverShadow} min-md:hover:ring-2 min-md:hover:${hoverRing}`}
                  aria-labelledby={`${item.slug}-title`}
                  onClick={() => setActiveItem({ ...item })}
                  onHoverStart={() =>
                    setIsHovering({
                      title: item.buttonTitle,
                      isHovered: true,
                      cursorTextColor: details.cursorTextColor,
                    })
                  }
                  onHoverEnd={() => setIsHovering({ title: item.buttonTitle, isHovered: false, cursorTextColor: '' })}
                >
                  <div className="flex items-center gap-x-5 md-lg:gap-x-2" aria-haspopup="dialog" aria-labelledby={`${item.slug}-title`}>
                    <SvgComponent className="size-12 md-lg:size-10" aria-hidden="true" />
                    <h2 id={`${item.slug}-title`} className="text-H3 font-bold text-white md-lg:text-[1.5em]">
                      {item.buttonTitle}
                    </h2>
                  </div>
                  {connectorHover && (
                    <div
                      className={`absolute -bottom-20 left-1/2 -z-10 h-20 w-[3px] transform bg-gray-600 transition-colors duration-300 md:bottom-0 md:h-20 md:translate-y-full md:border-l-2 md:border-gray-600 md:bg-transparent ${connectorHover}`}
                      aria-hidden="true"
                    />
                  )}
                </motion.button>
              </li>
            );
          })}
        </ul>

        {/* Bottom Card (Orchestrate) */}
        {bottomRowItem && <BottomProductCard item={bottomRowItem} setActiveItemData={setActiveItem} setIsHovering={setIsHovering} />}
      </nav>

      {/* Product Modal */}

      <AnimatePresence mode="sync">
        {activeItem && <ProductModal activeItemData={activeItem} setActiveItemData={setActiveItem} />}
      </AnimatePresence>
    </article>
  );
}

// New component for the bottom card
interface BottomProductCardProps {
  item: ContenfulProductItem;
  setActiveItemData: Dispatch<SetStateAction<ContenfulProductItem | null>>;
  setIsHovering: Dispatch<SetStateAction<{ title: string; isHovered: boolean; cursorTextColor: string }>>;
}

function BottomProductCard({ item, setActiveItemData, setIsHovering }: BottomProductCardProps) {
  const details = productDetailsMap[item.buttonTitle];
  if (!details) return null;
  const { SvgComponent, hoverShadow, hoverRing, focusRing } = details;
  // Handle potentially undefined route and slug for aria-labelledby if needed,
  // though title is likely sufficient here.
  const titleId = item.slug ? `${item.slug}-title` : `${item._id}-title`; // Fallback ID

  return (
    <ul
      className="h-[95px] w-full"
      aria-label={`${item.buttonTitle} product card`}
      style={{ alignContent: 'center' }}
    >
      <li className="contents">
        <motion.button
          className={`group relative flex h-full w-full touch-manipulation items-center justify-center rounded-lg border-[3px] border-gray-600 bg-SLATE_BLUE px-4 text-center transition-colors duration-300 focus:outline-none focus:ring-2 ${focusRing} focus:ring-offset-2 focus:ring-offset-SLATE_BLUE min-md:hover:bg-SLATE-BLUE-950 min-md:hover:shadow-[0_0_10px_rgba(0,0,0,0.5)] min-md:hover:${hoverShadow} min-md:hover:ring-2 min-md:hover:${hoverRing}`}
          aria-labelledby={titleId}
          onClick={() => setActiveItemData({ ...item })}
          onHoverStart={() =>
            setIsHovering({
              title: item.buttonTitle,
              isHovered: true,
              cursorTextColor: details.cursorTextColor,
            })
          }
          onHoverEnd={() => setIsHovering({ title: item.buttonTitle, isHovered: false, cursorTextColor: '' })}
        >
          <div className="flex items-center gap-x-5" aria-haspopup="dialog" aria-labelledby={titleId}>
            <SvgComponent className="size-12 md-lg:size-10" aria-hidden="true" />
            <h2 id={titleId} className="text-H3 font-bold text-white md-lg:text-[1.5em]">
              {item.buttonTitle}
            </h2>
          </div>
        </motion.button>
      </li>
    </ul>
  );
}

function ProductModal({
  activeItemData,
  setActiveItemData,
}: {
  activeItemData: any;
  setActiveItemData: Dispatch<SetStateAction<ContenfulProductItem | null>>;
}) {
  const details = productDetailsMap[activeItemData.buttonTitle];
  if (!details) return null;
  const { backgroundColor, borderColor } = details;
  const { buttonTitle, description, featuredImage, linkLabel, route, slug } = activeItemData;

  const href = getHref(route, slug);

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/80 p-4 text-WHITE backdrop-blur-sm"
      initial={{ opacity: 0, transition: { duration: 0.3 } }}
      animate={{ opacity: 1, transition: { duration: 0.3 } }}
      exit={{ opacity: 0, transition: { duration: 0.3, delay: 0.3 } }}
      onClick={() => setActiveItemData(null)}
      role="dialog"
      aria-modal="true"
      aria-label="Product details view"
    >
      <div className="flex h-full items-center justify-center">
        <div className="max-w-[55vw] md-lg:max-w-[75vw] md:max-w-[95vw]">
          <motion.button
            initial={{ opacity: 0, x: 100, y: -100 }}
            animate={{ opacity: 1, x: 0, y: 0, transition: { duration: 0.5 } }}
            exit={{ opacity: 0, x: 100, y: -100, transition: { duration: 0.3 } }}
            className="absolute right-4 top-4 z-[60] touch-manipulation rounded-full p-2 text-white transition-colors duration-300 hover:bg-gray-300/10 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300/80 active:text-PURP-200"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setActiveItemData(null);
            }}
            aria-label="Close product details view"
            title="Close product details view"
          >
            <XIcon className="size-8 text-GREEN-500 transition-all duration-300 hover:scale-110 focus:rounded-sm focus:ring-2 focus:ring-inset focus:ring-gray-300/80 active:text-gray-400" />
          </motion.button>

          <motion.div
            initial={{ opacity: 0, scale: 0.95, filter: 'blur(20px)', transition: { duration: 0.3, delay: 1 } }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)', transition: { duration: 0.3, delay: 0.3 } }}
            exit={{ opacity: 0, scale: 0.95, filter: 'blur(20px)', transition: { duration: 0.3 } }}
            className="relative w-full overflow-auto rounded-lg bg-white p-10 md-lg:p-5"
            style={{ backgroundColor, border: `2px solid ${borderColor}`, boxShadow: `0 0 10px 0px ${borderColor}` }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <figure className="mb-4 aspect-video overflow-hidden rounded">
              <Image
                src={featuredImage.url}
                alt={featuredImage.title || buttonTitle}
                width={featuredImage.width}
                height={featuredImage.height}
                className="h-full w-full object-cover"
              />
            </figure>
            <h1 className="mb-2 text-H2 font-bold">{buttonTitle}</h1>
            <p className="mb-4 text-Body">{description}</p>
            <Link
              href={href}
              className="bg-BLUE-500 hover:bg-BLUE-600 inline-block rounded py-2 text-white underline transition-colors hover:text-GREEN-500 focus:outline-none focus:ring-2 focus:ring-BLUE-400 focus:ring-offset-2"
            >
              {linkLabel}
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
